import type { Client } from '@repo/sdk'

import {
  PROXY_SESSION_ENDED_PATH,
  clearAuthData,
  getStoredToken,
  getTokenExpiresAt,
  isProxySession,
} from './auth-storage'
import { refreshTokenService } from './refreshTokenService'

const REFRESH_ENDPOINT = '/api/v1/users/refresh'
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000

/**
 * Replayable copy of each outgoing request that carries a body.
 *
 * `fetch(request)` disturbs the request body, so by the time the 401 response
 * interceptor runs the original body can no longer be read. A clone is taken
 * *before* the fetch and looked up here when a request has to be retried with a
 * refreshed token. Keyed weakly, so it disappears with the request object.
 */
const replayableRequests = new WeakMap<Request, Request>()

function hasBody(request: Request): boolean {
  return request.method !== 'GET' && request.method !== 'HEAD' && request.body !== null
}

export function setupTokenInterceptor(client: Client): void {
  client.interceptors.request.use(async (request) => {
    if (request.url.includes(REFRESH_ENDPOINT)) {
      return request
    }

    if (hasBody(request)) {
      try {
        replayableRequests.set(request, request.clone())
      } catch {
        // Body already consumed — the retry below will fall back to no body.
      }
    }

    if (isTokenExpiringSoon()) {
      const newAccessToken = await refreshTokenService.refresh()

      if (newAccessToken) {
        request.headers.set('Authorization', `Bearer ${newAccessToken}`)
      }
    }

    return request
  })

  client.interceptors.response.use(async (response, request) => {
    if (response.status !== 401) {
      return response
    }

    if (request.url.includes(REFRESH_ENDPOINT)) {
      handleRefreshFailure()
      return response
    }

    const authToken = getStoredToken()

    if (!authToken) {
      return response
    }

    const newAccessToken = await refreshTokenService.refresh()

    if (!newAccessToken) {
      handleRefreshFailure()
      return response
    }

    const retryResponse = await retryOriginalRequest(request, newAccessToken)

    return retryResponse
  })
}

function isTokenExpiringSoon(): boolean {
  const expiresAt = getTokenExpiresAt()
  const authToken = getStoredToken()

  if (!authToken || !expiresAt) {
    return false
  }

  return expiresAt - Date.now() < TOKEN_REFRESH_BUFFER_MS
}

function handleRefreshFailure(): void {
  const wasProxySession = isProxySession()
  clearAuthData()
  refreshTokenService.reset()
  // A super admin proxy tab (#273) cannot sign in again by itself — it says the session is over.
  if (wasProxySession) {
    globalThis.location.assign(PROXY_SESSION_ENDED_PATH)
    return
  }
  // Clearing storage alone left the dealer staring at a half-broken screen; send them to the
  // login page with the same `session_expired` flag / `?to=` contract the auth provider uses.
  if (globalThis.location.pathname === '/login') return
  localStorage.setItem('session_expired', 'true')
  const returnTo = globalThis.location.pathname + globalThis.location.search
  globalThis.location.assign(`/login?to=${encodeURIComponent(returnTo)}`)
}

async function retryOriginalRequest(
  originalRequest: Request,
  newAccessToken: string,
): Promise<Response> {
  const headers = new Headers(originalRequest.headers)
  headers.set('Authorization', `Bearer ${newAccessToken}`)

  // Reusing `originalRequest.body` (a ReadableStream) needs `duplex: 'half'` and
  // throws a TypeError in Chrome without it, so every POST/PATCH retried after a
  // token refresh failed. Replay the buffered clone captured before the fetch.
  const body = await readReplayBody(originalRequest)

  const retryRequest = new Request(originalRequest.url, {
    method: originalRequest.method,
    headers,
    ...(body === undefined ? {} : { body }),
    credentials: originalRequest.credentials,
    cache: originalRequest.cache,
    redirect: originalRequest.redirect,
    referrer: originalRequest.referrer,
    integrity: originalRequest.integrity,
    mode: originalRequest.mode,
  })

  return globalThis.fetch(retryRequest)
}

/** Buffered body for the retry, or `undefined` for bodyless (GET/HEAD) requests. */
async function readReplayBody(originalRequest: Request): Promise<ArrayBuffer | undefined> {
  if (!hasBody(originalRequest)) {
    return undefined
  }

  const replayable = replayableRequests.get(originalRequest)
  if (!replayable) {
    return undefined
  }

  try {
    return await replayable.arrayBuffer()
  } catch {
    return undefined
  }
}
